import React, { useEffect, useRef, useState, useContext } from 'react'
import { get } from '~/utils/api'
import { WebsocketContext } from './WebsocketConn'
import { ChatContext } from './chatConn'

export type InternalChatOnList = {
  userId: number
  updatedAt: { date: Date }
  unreadCount: number
  lastMessage: string
  online: boolean
}

type State = {
  chats: InternalChatOnList[]
  loadHook: () => void
  loading: boolean
  unreadChats: number
}

export const InternalChatManagerContext = React.createContext<State>({
  chats: [],
  loadHook: () => {},
  loading: false,
  unreadChats: 0,
})

type Props = { children: React.ReactNode }

const InternalChatManager: React.FC<Props> = ({ children }) => {
  const [loading, setLoading] = useState(false)
  const [initialized, setInitialized] = useState(false)
  const [items, setItems] = useState<InternalChatOnList[]>([])
  const [unreadChats, setUnreadChats] = useState<number>(0)

  const { isCampaignChannelLoaded, campaignChannel } =
    useContext(WebsocketContext)
  const { isConnected, channel: chatChannel } = useContext(ChatContext)

  const loadingRef = useRef(loading)
  const itemsRef = useRef(items)

  loadingRef.current = loading
  itemsRef.current = items

  const download = (params = {}, more = false) => {
    return new Promise<void>(r => {
      setLoading(true)
      loadingRef.current = true

      get<InternalChatOnList[]>(`internal-chat/list`, {
        ...params,
      }).then(newItems => {
        setItems(newItems)

        setItems(items =>
          items.map(item => {
            const user = window.c.users.find(user => user.id == item.userId)

            return { ...item, online: user ? user.online : false }
          })
        )

        setLoading(false)
        loadingRef.current = false
        r()
      })
    })
  }

  const setUnread = async () => {
    const data = await get<InternalChatOnList[]>(`internal-chat/list`)
    setUnreadChats(data.filter(item => item.unreadCount > 0).length)
  }

  useEffect(() => {
    !items.length
      ? setUnread()
      : setUnreadChats(items.filter(item => item.unreadCount > 0).length)
  }, [items])

  useEffect(() => {
    if (!isCampaignChannelLoaded) {
      return
    }

    campaignChannel?.on('presence', data => {
      if (data.type == 'online') {
        setItems(items =>
          items.map(item => {
            return data.users.includes(item.userId)
              ? { ...item, online: true }
              : item
          })
        )
      }

      if (data.type == 'offline') {
        setItems(items =>
          items.map(item => {
            return item.userId == data.user.id
              ? { ...item, online: false }
              : item
          })
        )
      }
    })
  }, [isCampaignChannelLoaded])

  useEffect(() => {
    if (!isConnected) {
      return
    }

    chatChannel.on('internalChatReadMessage', (data: any) => {
      setItems(items =>
        items.map(item =>
          item.userId == data.chatId
            ? {
                ...item,
                unreadCount: data.unreadCount,
              }
            : item
        )
      )
    })

    chatChannel.on('internalChatMessage', (data: any) => {
      const path = window.location.pathname.split('/').pop()

      setItems(items =>
        items.map(item =>
          item.userId == data.chatId
            ? {
                ...item,
                lastMessage: data.message.content,
                unreadCount:
                  !data.message.me && !(path == data.chatId)
                    ? item.unreadCount + 1
                    : item.unreadCount,
              }
            : item
        )
      )
    })
  }, [isConnected])

  const loadHook = () => {
    useEffect(() => {
      if (initialized) {
        return
      }

      download().then(() => {
        setInitialized(true)
      })
    }, [])
  }

  useEffect(() => {
    if (!initialized) {
      return
    }

    download()
  }, [])

  return (
    <InternalChatManagerContext.Provider
      value={{
        chats: items,
        loadHook,
        loading,
        unreadChats,
      }}
    >
      {children}
    </InternalChatManagerContext.Provider>
  )
}

export default InternalChatManager
